import React from "react";
import CustomerCard from "./CustomerCard";
import { useGameStateContext } from "../GameStateProvider";
import { useCustomerActions } from "../hooks/useCustomerActions";
import { ASSETS } from "../assetManifest.js";
import { CONSTANTS } from "../data";
import { getAllItemsMap, createResultToRecipeMap } from "../utils/dataUtils";

const getCustomerIcon = (type: string) => {
  switch (type) {
    case "kid":
      return ASSETS.icons.kid.path;
    case "critic":
      return ASSETS.icons.critic.path;
    default:
      return ASSETS.icons.customer.path;
  }
};

const CustomerQueue: React.FC = () => {
  const { state } = useGameStateContext();
  const { handleServeCustomer, canServeCustomerInTutorial } = useCustomerActions();

  // Use centralized map creation
  const allItemsMap = getAllItemsMap();
  const resultToRecipeMap = createResultToRecipeMap(state.discoveredRecipes);

  return (
    <aside
      data-tutorial-id="customer-queue"
      className="bg-surface p-2 sm:p-4 flex flex-col h-full mobile-customer-queue"
    >
      <h3 className="font-bold text-lg sm:text-xl mb-2 sm:mb-4 text-text-primary flex-shrink-0">
        <span className="block sm:hidden">Queue ({state.customers.filter((c) => c.active).length}/6)</span>
        <span className="hidden sm:block">Customer Queue ({state.customers.filter((c) => c.active).length}/6)</span>
      </h3>
      <div className="flex-1 overflow-y-auto space-y-2 sm:space-y-3 pr-1 sm:pr-2 -mr-1 sm:-mr-2">
        {state.customers.map(
          (customer) =>
            customer.active && (
              <CustomerCard
                key={customer.id}
                customer={customer}
                orderName={
                  allItemsMap.get(customer.order)?.name || customer.order
                }
                recipeHint={(() => {
                  const recipe = resultToRecipeMap.get(customer.order);
                  if (!recipe) return "No recipe";
                  const discovered =
                    recipe.discovered ||
                    recipe.id.startsWith("ai_") ||
                    false;
                  if (discovered) {
                    return recipe.ingredients
                      .map(
                        (ing: string) =>
                          allItemsMap.get(ing)?.name || ing
                      )
                      .join(" + ");
                  }
                  return "??? + ???";
                })()}
                canServe={state.inventory[customer.order] > 0}
                onServe={() => handleServeCustomer(customer.id, customer.order)}
                icon={getCustomerIcon(customer.type)}
                patienceBarColor={
                  canServeCustomerInTutorial(customer)
                    ? customer.patience / customer.maxPatience > 0.6
                      ? "bg-success"
                      : customer.patience / customer.maxPatience > 0.3
                      ? "bg-warning"
                      : "bg-danger"
                    : "bg-gray-400"
                }
                patiencePercent={
                  (customer.patience / customer.maxPatience) * 100
                }
                isCritic={customer.type === "critic"}
                isServeDisabled={!canServeCustomerInTutorial(customer)}
              />
            )
        )}
        {state.customers.filter((c) => c.active).length === 0 && (
          <div className="text-text-secondary text-center py-6 sm:py-10 text-sm">
            {state.hudHint ? state.hudHint : "No customers waiting"}
          </div>
        )}
      </div>
    </aside>
  );
};

export default CustomerQueue;
